/**
 * MRZ parser ICAO 9303
 *
 * Supporta:
 *   • TD3 (passaporto): 2 righe × 44 caratteri
 *   • TD1 (carta d'identità / CIE): 3 righe × 30 caratteri
 *   • TD2 (alcuni documenti residence): 2 righe × 36 caratteri
 *
 * Tutti i campi sono ASCII upper-case con padding `<`.
 * Sex: M | F | < (unspecified).
 * Date format: YYMMDD.
 *
 * NOTA: questo parser NON gestisce l'immagine. Riceve solo testo OCR.
 * L'immagine è cancellata dal canvas chiamante prima ancora che parseMRZ
 * venga eseguito ("Ciao ciao foto, qui non vi vogliamo dopo l'invio 🗑️").
 */

// ICAO 9303 country code → ISO 3 alpha-3 (subset, sufficient for our subset)
// Most are identity-mappings. The exceptions (D=DEU, GBD=GBR, etc.) are encoded.
const COUNTRY_FIXES = {
  D: 'DEU',
  GBD: 'GBR', GBN: 'GBR', GBO: 'GBR', GBP: 'GBR', GBS: 'GBR',
  EUE: 'EU',
  UNK: 'UNK',
  XXA: 'XXA',
};


function fixCountry(code) {
  if (!code) return '';
  const c = code.toUpperCase().replace(/</g, '');
  return COUNTRY_FIXES[c] || c;
}


/** YYMMDD (MRZ) → YYYY-MM-DD (ISO). Heuristic for 2-digit year. */
function parseMrzDate(yymmdd, isFutureLikely = false) {
  if (!yymmdd || yymmdd.length !== 6 || /[^0-9]/.test(yymmdd)) return '';
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  // Birth date: if YY > current year-2, it's 19YY (someone born last century).
  // Expiry/issue: if isFutureLikely, prefer 20YY.
  const now = new Date().getFullYear();
  const cy = now % 100;
  let yyyy;
  if (isFutureLikely) {
    yyyy = 2000 + yy;  // future expiries
  } else {
    yyyy = yy <= cy ? 2000 + yy : 1900 + yy;
  }
  return `${yyyy}-${mm}-${dd}`;
}


/** Strip trailing fillers and replace internal `<<` with space. */
function cleanName(s) {
  return (s || '').replace(/</g, ' ').replace(/\s+/g, ' ').trim();
}


/**
 * Parse a candidate MRZ string. Returns null if the format isn't recognized.
 * Returns: {
 *   doc_type: 'passport'|'id'|'unknown',
 *   nome: string,
 *   cognome: string,
 *   sesso: 'M'|'F'|'',
 *   birth_date: 'YYYY-MM-DD',
 *   document_number: string,
 *   document_expiry: 'YYYY-MM-DD',
 *   nationality_iso3: string,
 *   issuing_country_iso3: string,
 *   raw: string,
 * }
 */
export function parseMRZ(rawText) {
  if (!rawText) return null;

  // Sanitize: keep only allowed MRZ chars, uppercase, split into non-empty lines
  const lines = rawText
    .toUpperCase()
    .split(/[\r\n]+/)
    .map(l => l.replace(/[^A-Z0-9<]/g, '').trim())
    .filter(Boolean);

  if (lines.length < 2) return null;

  // Try TD3 first: 2 lines × 44 chars
  for (let i = 0; i + 1 < lines.length; i++) {
    const l1 = lines[i];
    const l2 = lines[i + 1];
    if (l1.length === 44 && l2.length === 44 && l1[0] === 'P') {
      try {
        return parseTD3(l1, l2);
      } catch (_) { /* continue */ }
    }
  }

  // Try TD1: 3 lines × 30 chars
  for (let i = 0; i + 2 < lines.length; i++) {
    const l1 = lines[i];
    const l2 = lines[i + 1];
    const l3 = lines[i + 2];
    if (l1.length === 30 && l2.length === 30 && l3.length === 30 && /^I/.test(l1)) {
      try {
        return parseTD1(l1, l2, l3);
      } catch (_) { /* continue */ }
    }
  }

  // Try TD2: 2 lines × 36 chars
  for (let i = 0; i + 1 < lines.length; i++) {
    const l1 = lines[i];
    const l2 = lines[i + 1];
    if (l1.length === 36 && l2.length === 36) {
      try {
        return parseTD2(l1, l2);
      } catch (_) { /* continue */ }
    }
  }

  return null;
}


// ─── TD3 (Passport) — 2×44 ────────────────────────────────────────────────
function parseTD3(l1, l2) {
  // Line 1: P<COUNTRY<<SURNAME<<GIVEN_NAMES<<<<...
  const docType = l1.slice(0, 2).replace(/</g, '');
  const issuing = fixCountry(l1.slice(2, 5));
  const namesField = l1.slice(5, 44);
  const [surnameRaw = '', givenRaw = ''] = namesField.split('<<', 2);

  // Line 2: passport_number(9) | check | nationality(3) | birth_yymmdd(6) | check | sex(1) | expiry_yymmdd(6) | check | personal_number(14) | check | composite_check
  const docNumber = l2.slice(0, 9).replace(/</g, '');
  const nationality = fixCountry(l2.slice(10, 13));
  const birth = parseMrzDate(l2.slice(13, 19), false);
  const sex = (l2.slice(20, 21) || '').replace(/[^MF]/g, '');
  const expiry = parseMrzDate(l2.slice(21, 27), true);

  return {
    doc_type: 'passport',
    cognome: cleanName(surnameRaw),
    nome: cleanName(givenRaw),
    sesso: sex || '',
    birth_date: birth,
    document_number: docNumber,
    document_expiry: expiry,
    nationality_iso3: nationality,
    issuing_country_iso3: issuing,
    icao_doc_code: docType,
    raw: l1 + '\n' + l2,
  };
}


// ─── TD1 (CIE / Resident card) — 3×30 ─────────────────────────────────────
function parseTD1(l1, l2, l3) {
  // Line 1: doc_code(2) | issuing(3) | doc_number(9) | check | optional(15)
  const docType = l1.slice(0, 2).replace(/</g, '');
  const issuing = fixCountry(l1.slice(2, 5));
  const docNumber = l1.slice(5, 14).replace(/</g, '');

  // Line 2: birth_yymmdd(6) | check | sex(1) | expiry_yymmdd(6) | check | nationality(3) | optional(11) | check
  const birth = parseMrzDate(l2.slice(0, 6), false);
  const sex = (l2.slice(7, 8) || '').replace(/[^MF]/g, '');
  const expiry = parseMrzDate(l2.slice(8, 14), true);
  const nationality = fixCountry(l2.slice(15, 18));

  // Line 3: surname<<given_names
  const [surnameRaw = '', givenRaw = ''] = l3.split('<<', 2);

  return {
    doc_type: 'id',
    cognome: cleanName(surnameRaw),
    nome: cleanName(givenRaw),
    sesso: sex || '',
    birth_date: birth,
    document_number: docNumber,
    document_expiry: expiry,
    nationality_iso3: nationality,
    issuing_country_iso3: issuing,
    icao_doc_code: docType,
    raw: l1 + '\n' + l2 + '\n' + l3,
  };
}


// ─── TD2 (older ID / EU residence) — 2×36 ─────────────────────────────────
function parseTD2(l1, l2) {
  const docType = l1.slice(0, 2).replace(/</g, '');
  const issuing = fixCountry(l1.slice(2, 5));
  const namesField = l1.slice(5, 36);
  const [surnameRaw = '', givenRaw = ''] = namesField.split('<<', 2);

  const docNumber = l2.slice(0, 9).replace(/</g, '');
  const nationality = fixCountry(l2.slice(10, 13));
  const birth = parseMrzDate(l2.slice(13, 19), false);
  const sex = (l2.slice(20, 21) || '').replace(/[^MF]/g, '');
  const expiry = parseMrzDate(l2.slice(21, 27), true);

  return {
    doc_type: docType.startsWith('I') ? 'id' : 'unknown',
    cognome: cleanName(surnameRaw),
    nome: cleanName(givenRaw),
    sesso: sex || '',
    birth_date: birth,
    document_number: docNumber,
    document_expiry: expiry,
    nationality_iso3: nationality,
    issuing_country_iso3: issuing,
    icao_doc_code: docType,
    raw: l1 + '\n' + l2,
  };
}


// ── ISO3 → ISTAT (mirror del backend, per pre-compilare il form) ─────────
const ISO_TO_ISTAT = {
  ITA: '100000100', FRA: '100000110', DEU: '100000132', GBR: '100000219',
  ESP: '100000134', PRT: '100000131', BEL: '100000103', NLD: '100000125',
  AUT: '100000102', CHE: '100000133', SWE: '100000136', DNK: '100000107',
  FIN: '100000109', NOR: '100000126', IRL: '100000118', POL: '100000129',
  CZE: '100000275', SVK: '100000276', HUN: '100000117', ROU: '100000130',
  BGR: '100000104', HRV: '100000252', SVN: '100000260', GRC: '100000114',
  EST: '100000247', LVA: '100000254', LTU: '100000255', MLT: '100000122',
  CYP: '100000105', LUX: '100000121', ISL: '100000119', SRB: '100000274',
  MNE: '100000280', ALB: '100000201', MKD: '100000256', BIH: '100000274',
  RUS: '100000262', UKR: '100000263', BLR: '100000264', MDA: '100000259',
  TUR: '100000218', USA: '100000400', CAN: '100000401', MEX: '100000405',
  BRA: '100000302', ARG: '100000301', CHL: '100000305', COL: '100000306',
  PER: '100000310', URY: '100000314', VEN: '100000315',
  CHN: '100000506', JPN: '100000522', KOR: '100000532', IND: '100000514',
  PAK: '100000529', BGD: '100000505', PHL: '100000531', THA: '100000540',
  VNM: '100000543', IDN: '100000513', MYS: '100000526', SGP: '100000536',
  ARE: '100000503', SAU: '100000534', ISR: '100000518',
  EGY: '100000610', MAR: '100000620', TUN: '100000627', DZA: '100000601',
  NGA: '100000623', ZAF: '100000634',
  AUS: '100000702', NZL: '100000704',
};

export function iso3ToIstat(iso3) {
  if (!iso3) return 'Z000';
  return ISO_TO_ISTAT[iso3.toUpperCase()] || 'Z000';
}


/** ICAO doc-type code (2 char) → app's doc_type enum. */
export function icaoToDocType(icao) {
  const c = (icao || '').toUpperCase().replace(/</g, '');
  if (c.startsWith('P')) return 'passaporto';
  if (c === 'IC' || c === 'AC' || c === 'AR') return 'cart_ident_el';
  if (c.startsWith('I')) return 'identita';
  if (c === 'DL') return 'patente';
  if (c.startsWith('R') || c === 'RP') return 'perm_soggiorno';
  return 'identita';
}
